import { useCallback, useEffect, useMemo, useState } from "react";
import X2JS from "x2js";
import { fetchProductsByVin } from "../../api/showMeTheParts";
import Dropdown from "../../common/components/Dropdown";
import {
  SelectorsLocalStorage,
  SptLocalStorageVariables,
} from "../../common/constants/localStorage";
import {
  getLocalStorageValue,
  setLocalStorageValue,
} from "../../common/helpers";
import { Wrapper, SearchButtonStyled, VinElementsWrapper } from "./styled";
import { Input } from "../../common/components";
import { useGlobalContext } from "../../context";
import {
  ButtonWrapper,
  ClearButtonStyled,
  FullWidth,
} from "../YearMakeModel/styled";

const defaultState = {
  value: "",
  label: "",
  isLoading: false,
  isError: false,
};

const VIN = ({ openApp }: { openApp?: boolean }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [vin, setVin] = useState<string>("");
  const [productOptions, setProductOptions] = useState<any>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const x2js = new X2JS();

  const { state, dispatch } = useGlobalContext();

  const {
    selectorModalConfig,
    year,
    make,
    model,
    engine,
    engineOptions,
    vinData,
    vinProduct,
  } = state;

  const hidePartType = selectorModalConfig?.hide_vin_part_type;

  const licenseKey = selectorModalConfig?.license_key;
  const databaseUrl = selectorModalConfig?.database_url;

  useEffect(() => {
    const storageVin = getLocalStorageValue(
      SelectorsLocalStorage.SELECTED_VIN_VALUE_NAME
    );
    setVin(storageVin ?? "");
    const storageVinProductName = getLocalStorageValue(
      SelectorsLocalStorage.SELECTED_VIN_PRODUCT_NAME
    );
    const storageVinProductValue = getLocalStorageValue(
      SelectorsLocalStorage.SELECTED_VIN_PRODUCT_VALUE
    );
    if (storageVinProductName && storageVinProductValue) {
      dispatch({
        type: "setVinProduct",
        payload: {
          value: storageVinProductValue,
          label: storageVinProductName,
        },
      });
    }

    const productOptions = localStorage.getItem(
      SelectorsLocalStorage.VIN_PRODUCT_OPTIONS
    )
      ? JSON.parse(
          localStorage.getItem(SelectorsLocalStorage.VIN_PRODUCT_OPTIONS) || ""
        )
      : [];
    setProductOptions(productOptions);
  }, []);

  useEffect(() => {
    if (vin.length === 17 && !hidePartType) {
      getProducts();
    }
  }, [vin]);

  const priorities = selectorModalConfig?.product_category_priorities;

  const getPriorityIndex = (item: any) => {
    // Compare lowercase versions for case-insensitive matching
    const index = priorities.findIndex(
      (priority: string) => priority.toLowerCase() === item.label.toLowerCase()
    );
    // If not found in priorities, return a large number
    return index === -1 ? priorities.length : index;
  };

  const options: any = useMemo(() => {
    if (!vinData?.products?.productdata.length) {
      const item = vinData?.products?.productdata;
      if (item?.id) {
        return [{ value: item?.id, label: item?.data }];
      } else {
        return [];
      }
    }
    return vinData?.products?.productdata.map((item: any) => ({
      value: item.id,
      label: item.data,
    }));
  }, [vinData]);

  const sortedOptions = options?.slice().sort((a: any, b: any) => {
    const priorityA = getPriorityIndex(a);
    const priorityB = getPriorityIndex(b);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.label.localeCompare(b.label);
  });
  const sortedProductOptions = productOptions
    ?.slice()
    .sort((a: any, b: any) => {
      const priorityA = getPriorityIndex(a);
      const priorityB = getPriorityIndex(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return a.label.localeCompare(b.label);
    });

  useEffect(() => {
    if (options.length) {
      setLocalStorageValue(
        SelectorsLocalStorage.VIN_PRODUCT_OPTIONS,
        JSON.stringify(options)
      );
    }
  }, [options]);

  const getProducts = useCallback(async () => {
    if (!licenseKey || !databaseUrl) return;
    setLocalStorageValue(SelectorsLocalStorage.SELECTED_VIN_VALUE_NAME, vin);
    setIsLoading(true);
    dispatch({ type: "setData", payload: null });
    const response = await fetchProductsByVin({
      id: licenseKey,
      url: databaseUrl,
      vin: vin,
    });

    const data: any = x2js?.xml2js(response?.data);
    const vinResp = data?.ShowMeTheParts_App_Products?.app?.app_data;

    if (vinResp) {
      dispatch({
        type: "setYear",
        payload: {
          ...year,
          value: vinResp.year,
          label: vinResp.year,
        },
      });

      dispatch({
        type: "setMake",
        payload: {
          ...make,
          value: vinResp.makeid,
          label: vinResp.make,
        },
      });

      dispatch({
        type: "setModel",
        payload: {
          ...model,
          value: vinResp.modelid,
          label: vinResp.model,
        },
      });
      dispatch({ type: "setVinData", payload: vinResp });
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setIsError(true);
      dispatch({ type: "setData", payload: null });
    }
  }, [licenseKey, databaseUrl, vin]);

  const handleChangeVin = useCallback(
    (value: string) => {
      if (value !== vin) {
        setVin(value);
        dispatch({ type: "setVinData", payload: null });
        dispatch({
          type: "setVinProduct",
          payload: { value: "", label: "" },
        });
        setProductOptions(null);
      }
    },
    [vin]
  );

  const handleSearch = () => {
    if (openApp) {
      let mainElement: HTMLElement | null =
        document.getElementById("spt-react-app");
      if (!!mainElement) {
        mainElement!.style.display = "block";
      }
    }
    if (vinData) {
      sessionStorage.removeItem(SptLocalStorageVariables.SPT_FILTERS);
      setLocalStorageValue(SelectorsLocalStorage.SELECTED_VIN_VALUE_NAME, vin);
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_VIN_PRODUCT_NAME,
        vinProduct.label
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_VIN_PRODUCT_VALUE,
        vinProduct.value
      );

      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_YEAR_KEY_NAME,
        year.value
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_YEAR_VALUE_NAME,
        year.label
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MAKE_KEY_NAME,
        make.value
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MAKE_VALUE_NAME,
        make.label
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MODEL_KEY_NAME,
        model.value
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MODEL_VALUE_NAME,
        model.label
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_PARTTYPE_KEY_NAME,
        ""
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_PARTTYPE_VALUE_NAME,
        ""
      );
      setLocalStorageValue(SelectorsLocalStorage.SELECTED_PRODUCT_KEY_NAME, "");
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_PRODUCT_VALUE_NAME,
        ""
      );
      setLocalStorageValue(SelectorsLocalStorage.SELECTED_ENGINE_KEY_NAME, "");
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_ENGINE_VALUE_NAME,
        ""
      );
      localStorage.setItem(
        SptLocalStorageVariables.SPT_SELECTORS,
        JSON.stringify({
          year: year.value,
          make: make.value,
          model: model.value,
          product: "",
          engine: "",
        })
      );
      dispatch({
        type: "setSearchTerm",
        payload: {
          year: vinData.yearid,
          make: vinData.makeid,
          model: vinData.modelid,
          product: vinProduct.value,
          engine: vinData.engineid,
          search_type: "vin",
        },
      });
      dispatch({ type: "setCollapsed", payload: true });

      dispatch({
        type: "setYear",
        payload: { ...year, value: vinData.yearid, label: vinData.year },
      });
      dispatch({
        type: "setMake",
        payload: { ...make, value: vinData.makeid, label: vinData.make },
      });
      dispatch({
        type: "setModel",
        payload: { ...model, value: vinData.modelid, label: vinData.model },
      });
      dispatch({
        type: "setProduct",
        payload: { ...vinProduct, isLoading: false, isError: false },
      });
      if (Array.isArray(engineOptions)) {
        const currentEngine = engineOptions.filter(
          (e) => e.id === vinData.engineid
        )[0];
        dispatch({
          type: "setEngine",
          payload: {
            ...engine,
            value: vinData.engineid,
            label: currentEngine.data,
          },
        });
      } else {
        dispatch({
          type: "setEngine",
          payload: {
            ...engine,
            value: vinData.engineid,
            label: engineOptions.data,
          },
        });
      }
    }
  };

  const handleImmediateSearch = async () => {
    if (openApp) {
      let mainElement: HTMLElement | null =
        document.getElementById("spt-react-app");
      if (!!mainElement) {
        mainElement!.style.display = "block";
      }
    }
    if (!licenseKey || !databaseUrl) return;
    setLocalStorageValue(SelectorsLocalStorage.SELECTED_VIN_VALUE_NAME, vin);
    setIsLoading(true);
    const response = await fetchProductsByVin({
      id: licenseKey,
      url: databaseUrl,
      vin: vin,
    });
    setIsLoading(false);

    const data: any = x2js?.xml2js(response?.data);
    const vinResp = data?.ShowMeTheParts_App_Products?.app?.app_data;

    if (vinResp) {
      dispatch({
        type: "setYear",
        payload: {
          ...year,
          value: vinResp.year,
          label: vinResp.year,
        },
      });

      dispatch({
        type: "setMake",
        payload: {
          ...make,
          value: vinResp.makeid,
          label: vinResp.make,
        },
      });

      dispatch({
        type: "setModel",
        payload: {
          ...model,
          value: vinResp.modelid,
          label: vinResp.model,
        },
      });
      dispatch({ type: "setVinData", payload: vinResp });
    } else {
      setIsError(true);
      dispatch({ type: "setData", payload: null });
    }

    if (vinResp) {
      sessionStorage.removeItem(SptLocalStorageVariables.SPT_FILTERS);
      setLocalStorageValue(SelectorsLocalStorage.SELECTED_VIN_VALUE_NAME, vin);
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_YEAR_KEY_NAME,
        vinResp.year
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_YEAR_VALUE_NAME,
        vinResp.yearid
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MAKE_KEY_NAME,
        vinResp.makeid
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MAKE_VALUE_NAME,
        vinResp.make
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MODEL_KEY_NAME,
        vinResp.modelid
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_MODEL_VALUE_NAME,
        vinResp.model
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_PARTTYPE_KEY_NAME,
        ""
      );
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_PARTTYPE_VALUE_NAME,
        ""
      );
      setLocalStorageValue(SelectorsLocalStorage.SELECTED_PRODUCT_KEY_NAME, "");
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_PRODUCT_VALUE_NAME,
        ""
      );
      setLocalStorageValue(SelectorsLocalStorage.SELECTED_ENGINE_KEY_NAME, "");
      setLocalStorageValue(
        SelectorsLocalStorage.SELECTED_ENGINE_VALUE_NAME,
        ""
      );
      localStorage.setItem(
        SptLocalStorageVariables.SPT_SELECTORS,
        JSON.stringify({
          year: vinResp.value,
          make: vinResp.value,
          model: vinResp.value,
          product: "",
          engine: "",
        })
      );
      dispatch({
        type: "setSearchTerm",
        payload: {
          year: vinResp.yearid,
          make: vinResp.makeid,
          model: vinResp.modelid,
          product: "",
          engine: vinResp.engineid,
          search_type: "vin",
        },
      });

      dispatch({
        type: "setYear",
        payload: { ...year, value: vinResp.yearid, label: vinResp.year },
      });
      dispatch({
        type: "setMake",
        payload: { ...make, value: vinResp.makeid, label: vinResp.make },
      });
      dispatch({
        type: "setModel",
        payload: { ...model, value: vinResp.modelid, label: vinResp.model },
      });
      dispatch({
        type: "setProduct",
        payload: { ...vinProduct, isLoading: false, isError: false },
      });
      if (Array.isArray(engineOptions)) {
        const currentEngine = engineOptions.filter(
          (e) => e.id === vinResp.engineid
        )[0];
        dispatch({
          type: "setEngine",
          payload: {
            ...engine,
            value: vinResp.engineid,
            label: currentEngine.data,
          },
        });
      } else {
        dispatch({
          type: "setEngine",
          payload: {
            ...engine,
            value: vinResp.engineid,
            label: engineOptions.data,
          },
        });
      }
    }
  };

  const handleClear = () => {
    setVin("");
    dispatch({ type: "setVinData", payload: null });
    setProductOptions(null);
    dispatch({
      type: "setVinProduct",
      payload: { value: "", label: "" },
    });
    dispatch({ type: "setYear", payload: defaultState });
    dispatch({ type: "setMake", payload: defaultState });
    dispatch({ type: "setModel", payload: defaultState });
    dispatch({ type: "setProduct", payload: defaultState });
    dispatch({ type: "setProductCategory", payload: defaultState });
    dispatch({ type: "setEngine", payload: defaultState });

    dispatch({ type: "setMakeOptions", payload: [] });
    dispatch({ type: "setModelOptions", payload: [] });
    dispatch({ type: "setProductOptions", payload: [] });
    dispatch({ type: "setProductCategoriesOptions", payload: [] });
    dispatch({ type: "setEngineOptions", payload: [] });

    dispatch({ type: "setSearchTerm", payload: {} });
    dispatch({ type: "setLicensePlateSearch", payload: false });
    dispatch({ type: "setData", payload: null });

    const keysToRemove = [
      SelectorsLocalStorage.SELECTED_YEAR_KEY_NAME,
      SelectorsLocalStorage.SELECTED_YEAR_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_MAKE_KEY_NAME,
      SelectorsLocalStorage.SELECTED_MAKE_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_MODEL_KEY_NAME,
      SelectorsLocalStorage.SELECTED_MODEL_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_PARTTYPE_KEY_NAME,
      SelectorsLocalStorage.SELECTED_PARTTYPE_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_PRODUCT_KEY_NAME,
      SelectorsLocalStorage.SELECTED_PRODUCT_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_ENGINE_KEY_NAME,
      SelectorsLocalStorage.SELECTED_ENGINE_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_VIN_PRODUCT_NAME,
      SelectorsLocalStorage.SELECTED_VIN_PRODUCT_VALUE,
      SelectorsLocalStorage.SELECTED_VIN_VALUE_NAME,
      SelectorsLocalStorage.SELECTED_VIN_KEY_NAME,
      SelectorsLocalStorage.VIN_PRODUCT_OPTIONS,
      SelectorsLocalStorage.CURRENT_LICENSE_PLATE,
      SelectorsLocalStorage.SELECTED_LICENSE_PLATE_STATE,
      SelectorsLocalStorage.CURRENT_FITMENT_PART_DATA,
      SelectorsLocalStorage.LICENSE_PLATE_PRODUCT_LIST,
      SelectorsLocalStorage.LICENSE_PLATE_PRODUCT_SELECTED_NAME,
      SelectorsLocalStorage.LICENSE_PLATE_PRODUCT_SELECTED_VALUE,
      SptLocalStorageVariables.SPT_PRODUCTS,
      SptLocalStorageVariables.SPT_FILTERS,
      SptLocalStorageVariables.SPT_SELECTORS,
      SptLocalStorageVariables.SPT_SELECTED_FILTERS,
    ];

    keysToRemove.forEach((k) => localStorage.removeItem(k));
  };

  useEffect(() => {
    if (isError) {
      const timer = setTimeout(() => {
        setIsError(false);
      }, 5000); // 5000ms = 5 seconds

      // Clear the timer if the component unmounts or if isError changes before 5 seconds
      return () => clearTimeout(timer);
    }
  }, [isError]);

  const vinLabel = [vinData?.year, vinData?.make, vinData?.model]
    .filter((item) => !!item)
    .join(" ");

  return (
    <FullWidth>
      {isError ? (
        <div className="search-bar-active-tab-class">
          No records found, please check the VIN and retry
        </div>
      ) : (
        <></>
      )}
      <Wrapper>
        <VinElementsWrapper>
          <Input
            id="VIN"
            label={
              vinLabel ? `VIN (17 digits) - ${vinLabel}` : "VIN (17 digits)"
            }
            value={vin}
            onChange={(e) => {
              handleChangeVin(e.target.value);
            }}
            placeholder="VIN"
            maxLength={17}
          />
          {hidePartType && (
            <ButtonWrapper>
              <SearchButtonStyled
                className="search-bar-search-button-class"
                type="button"
                onClick={hidePartType ? handleImmediateSearch : getProducts}
                disabled={vin?.length !== 17 || isLoading}
              >
                {hidePartType ? "Search" : "Load Product Categories"}
              </SearchButtonStyled>
              <ClearButtonStyled
                type="button"
                className="search-bar-clear-button"
                onClick={handleClear}
                disabled={!year.value}
              >
                Clear
              </ClearButtonStyled>
            </ButtonWrapper>
          )}
        </VinElementsWrapper>
        {!hidePartType && (
          <VinElementsWrapper>
            <Dropdown
              value={vinProduct.label}
              onChange={({ value, label }) => {
                dispatch({
                  type: "setVinProduct",
                  payload: { value, label },
                });
              }}
              optionsList={
                options.length ? sortedOptions : sortedProductOptions
              }
              placeholder={isLoading ? "Loading..." : "Select Product Category"}
              searchPlaceholder="Search..."
              disabled={!sortedOptions.length}
              name="Product"
            />
            <ButtonWrapper>
              <SearchButtonStyled
                type="button"
                className="search-bar-search-button-class"
                onClick={handleSearch}
                disabled={!vinProduct.value || !vinData}
              >
                Search
              </SearchButtonStyled>
              <ClearButtonStyled
                type="button"
                className="search-bar-clear-button"
                onClick={handleClear}
                disabled={!year.value}
              >
                Clear
              </ClearButtonStyled>
            </ButtonWrapper>
          </VinElementsWrapper>
        )}
      </Wrapper>
    </FullWidth>
  );
};

export default VIN;
